/**
 * Jubly SalesDrive — Companies Page
 * List + detail view with Add Company modal
 */

import { useState, useEffect } from 'react';
import { Building2, Search, Plus, ArrowLeft, Globe, Users, X, Loader2, Edit2 } from 'lucide-react';

// ─── Company Modal ────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '', domain: '', industry: '', employeeCount: '', phone: '', notes: '',
};

function CompanyModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Company name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {initial?.id ? 'Edit Company' : 'New Company'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Company Name <span className="text-red-500">*</span></label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="Acme Corp" autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Domain</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.domain} onChange={e => set('domain', e.target.value)}
                placeholder="acme.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Industry</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.industry} onChange={e => set('industry', e.target.value)}
                placeholder="Technology"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Employees</label>
              <input
                type="number" min="1"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.employeeCount} onChange={e => set('employeeCount', e.target.value)}
                placeholder="50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
              <input
                type="tel"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.phone} onChange={e => set('phone', e.target.value)}
                placeholder="+1 555 000 0000"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Any notes…"
            />
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {initial?.id ? 'Save Changes' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalesCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => { loadCompanies(); }, [search]);

  async function loadCompanies() {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    try {
      const r = await fetch(`/api/crm/companies?${params}`, { credentials: 'include' });
      const d = await r.json();
      setCompanies(d.companies || []);
    } catch {}
    finally { setLoading(false); }
  }

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    fetch(`/api/crm/companies/${selectedId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setDetail(d))
      .catch(() => {});
  }, [selectedId]);

  async function handleCreate(form) {
    const res = await fetch('/api/crm/companies', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name,
        domain: form.domain || undefined,
        industry: form.industry || undefined,
        employeeCount: form.employeeCount ? parseInt(form.employeeCount, 10) : undefined,
        phone: form.phone || undefined,
        notes: form.notes || undefined,
      }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to create company');
    loadCompanies();
  }

  async function handleUpdate(form) {
    const res = await fetch(`/api/crm/companies/${selectedId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name,
        domain: form.domain || undefined,
        industry: form.industry || undefined,
        employeeCount: form.employeeCount ? parseInt(form.employeeCount, 10) : undefined,
        phone: form.phone || undefined,
        notes: form.notes || undefined,
      }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to update company');
    // Refresh detail
    const updated = await res.json().catch(() => null);
    if (updated) setDetail(updated);
    loadCompanies();
  }

  // ── Detail View ─────────────────────────────────────────────────────────────
  if (selectedId && detail) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <button onClick={() => setSelectedId(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <Building2 size={24} className="text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-gray-900">{detail.name}</h2>
              {detail.domain && (
                <a href={`https://${detail.domain}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline">{detail.domain}</a>
              )}
            </div>
            <button
              onClick={() => setShowEdit(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {detail.industry && (
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[10px] text-gray-400">Industry</p>
                <p className="text-sm font-medium">{detail.industry}</p>
              </div>
            )}
            {detail.employee_count && (
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[10px] text-gray-400">Employees</p>
                <p className="text-sm font-medium">{detail.employee_count}</p>
              </div>
            )}
            {detail.phone && (
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[10px] text-gray-400">Phone</p>
                <p className="text-sm font-medium">{detail.phone}</p>
              </div>
            )}
            {detail.annual_revenue && (
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[10px] text-gray-400">Revenue</p>
                <p className="text-sm font-medium">${(detail.annual_revenue / 100).toLocaleString()}</p>
              </div>
            )}
          </div>
          {detail.notes && <p className="text-sm text-gray-600">{detail.notes}</p>}
        </div>

        {showEdit && (
          <CompanyModal
            initial={{
              id: detail.id,
              name: detail.name || '',
              domain: detail.domain || '',
              industry: detail.industry || '',
              employeeCount: detail.employee_count || '',
              phone: detail.phone || '',
              notes: detail.notes || '',
            }}
            onClose={() => setShowEdit(false)}
            onSave={handleUpdate}
          />
        )}
      </div>
    );
  }

  // ── List View ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Company
        </button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search companies…"
          className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      ) : (
        <div className="space-y-2">
          {companies.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Building2 className="w-10 h-10 mb-3 stroke-1" />
              <p className="text-sm mb-3">No companies yet</p>
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
              >
                <Plus className="w-4 h-4" /> Add your first company
              </button>
            </div>
          )}
          {companies.map(c => (
            <button
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              className="w-full bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3 hover:shadow-sm hover:border-gray-300 transition-all text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Building2 size={18} className="text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">{c.name}</h3>
                <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                  {c.domain && <span className="flex items-center gap-1"><Globe size={10} />{c.domain}</span>}
                  {c.industry && <span>{c.industry}</span>}
                  {c.employee_count && <span className="flex items-center gap-1"><Users size={10} />{c.employee_count} employees</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showModal && (
        <CompanyModal
          onClose={() => setShowModal(false)}
          onSave={handleCreate}
        />
      )}
    </div>
  );
}
